import React, { useState } from 'react';
import { Select, DatePicker, Slider, Form, Input } from 'antd';
import { ButtonCommon } from 'components/common';
import { CaretDown, Calendar, Funnel, XCircle } from '@phosphor-icons/react';

import { formatPrice } from 'utils';
import { Warehouse } from 'models';

import { StockOutOrderFilter, StockOutOrderStatusCode, StockOutOrderType } from 'models/InventoryDocument';

interface FilterStockOutProps {
  initialFilter?: StockOutOrderFilter;
  onApplyFilter: (filter: StockOutOrderFilter) => void;
  onClearFilter: () => void;
  warehouses?: Warehouse[];
}

const FilterStockOut: React.FC<FilterStockOutProps> = ({
  initialFilter,
  onApplyFilter,
  onClearFilter,
  warehouses = [],
}) => {
  const [form] = Form.useForm();
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 150000000]);

  const handleClearFilter = () => {
    form.resetFields();
    setPriceRange(priceRange);
    form.setFieldsValue({
      createdAt: null,
      deliveryDate: null,
      warehouseId: undefined,
      code: undefined,
      status: undefined,
      stockOutOrderType: undefined,
      codeSearch: undefined,
    });
    onClearFilter();
  };

  const handleApplyFilter = () => {
    const values = form.getFieldsValue();
    onApplyFilter({
      ...values,
      priceRange,
    });
  };

  return (
    <div className='bg-white rounded-lg overflow-hidden w-full '>
      <div className='flex justify-between items-center px-6 py-3'>
        <h2 className='text-xl font-bold text-[var(--textBlack100)]'>Bộ lọc</h2>
      </div>

      <div className='px-6 py-3'>
        <Form form={form} layout='vertical' initialValues={initialFilter} className='filter-form'>
          <div className='flex flex-col'>
            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>
                  Thời gian tạo phiếu
                </span>
              }
              name='createdAt'
            >
              <DatePicker
                className='w-full'
                placeholder='Ngày - tháng - năm'
                format='DD/MM/YYYY'
                suffixIcon={<Calendar className='text-[var(--textBlack100)]' />}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>
                  Thời gian xuất kho
                </span>
              }
              name='deliveryDate'
            >
              <DatePicker
                className='w-full'
                placeholder='Ngày - tháng - năm'
                format='DD/MM/YYYY'
                suffixIcon={<Calendar className='text-[var(--textBlack100)]' />}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>Mã phiếu </span>
              }
              name='codeSearch'
            >
              <Input
                placeholder='Nhập mã phiếu xuất kho'
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                className='w-full'
              />
            </Form.Item>
            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>Kho xuất</span>
              }
              name='warehouseId'
            >
              <Select
                placeholder='Chọn kho xuất'
                className='w-full'
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                suffixIcon={<CaretDown color='black' size={18} />}
                options={warehouses?.map((warehouse: Warehouse) => ({
                  value: warehouse.id,
                  label: warehouse.name,
                }))}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>
                  Loại phiếu xuất
                </span>
              }
              name='stockOutOrderType'
            >
              <Select
                placeholder='Chọn loại phiếu'
                className='w-full'
                defaultValue=''
                suffixIcon={<CaretDown color='black' size={18} />}
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                options={[
                  { value: '', label: 'Tất cả ' },
                  { value: StockOutOrderType.SALE, label: 'Bán hàng' },
                  { value: StockOutOrderType.PRODUCTION, label: 'Sản xuất' },
                  { value: StockOutOrderType.TRANSFER, label: 'Chuyển kho' },
                  { value: StockOutOrderType.RETURN, label: 'Trả hàng' },
                  { value: StockOutOrderType.WASTE, label: 'Hàng hỏng' },
                  { value: StockOutOrderType.SAMPLE, label: 'Mẫu thử' },
                  { value: StockOutOrderType.OTHER, label: 'Khác' },
                ]}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>
                  Trạng thái phiếu
                </span>
              }
              name='status'
            >
              <Select
                placeholder='Chọn trạng thái'
                className='w-full'
                defaultValue={''}
                suffixIcon={<CaretDown color='black' size={18} />}
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                options={[
                  { value: '', label: 'Tất cả trạng thái' },
                  { value: StockOutOrderStatusCode.DRAFT, label: 'Bản nháp' },
                  { value: StockOutOrderStatusCode.WAIT_CONFIRM, label: 'Đợi duyệt phiếu' },
                  { value: StockOutOrderStatusCode.CONFIRMED, label: 'Đã duyệt phiếu' },
                  { value: StockOutOrderStatusCode.EXPORTED, label: 'Đã xuất kho' },
                  { value: StockOutOrderStatusCode.CANCELLED, label: 'Phiếu bị từ chối' },
                ]}
              />
            </Form.Item>

            <div className='mb-[24px]'>
              <p className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)] mb-2'>Khoảng giá</p>
              <Slider
                range
                min={0}
                max={150000000}
                value={priceRange}
                onChange={(value) => setPriceRange(value as [number, number])}
                className=' my-2'
                trackStyle={[{ backgroundColor: 'var(--primaryColor500)' }]}
                handleStyle={[
                  {
                    borderColor: 'var(--primaryColor500)',
                    backgroundColor: 'var(--primaryColor500)',
                    boxShadow: '0px 2px 5px rgba(38, 51, 77, 0.03)',
                  },
                  {
                    borderColor: 'var(--primaryColor500)',
                    backgroundColor: 'var(--primaryColor500)',
                    boxShadow: '0px 2px 5px rgba(38, 51, 77, 0.03)',
                  },
                ]}
                railStyle={{ backgroundColor: 'var(--primaryColor50)' }}
              />
              <div className='flex justify-between'>
                <div>
                  <span className='text-[12px] font-medium text-[var(--primaryColor500)]'>
                    {formatPrice(priceRange[0])}
                  </span>
                  <span className='text-[12px] font-medium text-[var(--primaryColor300)] ml-1'>VND</span>
                </div>
                <div>
                  <span className='text-[12px] font-medium text-[var(--primaryColor500)]'>
                    {formatPrice(priceRange[1])}
                  </span>
                  <span className='text-[12px] font-medium text-[var(--primaryColor300)] ml-1'>VND</span>
                </div>
              </div>
            </div>
          </div>
        </Form>

        <div className='flex gap-6'>
          <ButtonCommon
            color='none'
            size='sm'
            radius='rounded'
            className='min-w-[112px] hover:font-normal'
            onClick={handleClearFilter}
          >
            Xóa bộ lọc
          </ButtonCommon>
          <ButtonCommon
            color='primary'
            size='sm'
            radius='rounded'
            fullWidth
            className='flex justify-center items-center'
            onClick={handleApplyFilter}
            icon={<Funnel size={16} />}
            iconPosition='right'
          >
            Áp dụng bộ lọc
          </ButtonCommon>
        </div>
      </div>
    </div>
  );
};

export default FilterStockOut;
